/**
 * Final Rounds modals — round template, propose times, book (the cascade
 * confirmation) and the post-booking candidate summary draft.
 *
 * Same Modal/ModalContent sizing constants as ActionModals.tsx (see the
 * comments there for why the size overrides exist).
 */
import { useState } from 'react';
import { Input } from '@finn-auto/design-system/atoms/input';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
} from '@finn-auto/design-system/atoms/modal';
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import type { FinalRoundConfigInput, FinalRoundSuggestion } from '../../services/taActionBoardApi';
import type {
  BookFinalRoundRequest,
  ChainOption,
  RoleBoard,
  SchedulingCandidate,
  StageRef,
} from '../../types/taActionBoard';
import type { BookFinalRoundResponse } from '../../types/taFinalRounds';
import { DebriefDecisionBody, type DebriefOutcome } from './DebriefDecisionBody';
import { EmailLibraryLink } from './EmailLibraryLink';
import { LanguageToggle, type DraftLanguage } from './LanguageToggle';
import { SendFallback } from './SendFallback';
import { AskPanelBody } from './AskPanelBody';
import { BookBody } from './FinalRoundBookBody';
import { ConfigBody } from './FinalRoundConfigBody';
import { chainAsSlot } from './format';
import { ModalActions } from './ModalActions';
import { OfferedBody, type OfferChannel, type OfferedEmail } from './OfferedModalBody';

export type FinalRoundModalState =
  | { kind: 'fr-config'; role: RoleBoard; suggestion?: FinalRoundSuggestion | null }
  | {
      kind: 'fr-ask';
      role: RoleBoard;
      /** Whose round the panel is being asked about (may be unknown). */
      candidate: SchedulingCandidate | null;
      options: ChainOption[];
    }
  | {
      kind: 'fr-propose';
      role: RoleBoard;
      candidate: SchedulingCandidate;
      /** Chain options ticked in the tab — opened pre-selected. */
      preselected?: string[];
    }
  | { kind: 'fr-book'; role: RoleBoard; candidate: SchedulingCandidate }
  | { kind: 'fr-withdraw'; role: RoleBoard; candidate: SchedulingCandidate }
  | {
      kind: 'fr-debrief';
      role: RoleBoard;
      candidate: SchedulingCandidate;
      /** Lever's stages in pipeline order — the offer target is picked from these. */
      stages: StageRef[];
      /** ACTIVE Lever archive reasons, byte-exact (the archive resolves text→id). */
      archiveReasons: string[];
    }
  | {
      kind: 'fr-summary';
      candidate: SchedulingCandidate;
      // The response type, so the Email Library key cannot drift off this state shape.
      summary: BookFinalRoundResponse['summary'];
      /** Both languages, rendered at booking time — see the toggle in SummaryBody. */
      summaries?: BookFinalRoundResponse['summaries'];
      summaryLanguage?: DraftLanguage;
      candidateEmail: string | null;
    };

export type BookPayload = Omit<BookFinalRoundRequest, 'postingId' | 'opportunityId' | 'boardEmail'>;

export interface FinalRoundHandlers {
  onSaveConfig: (role: RoleBoard, config: FinalRoundConfigInput) => void;
  onPropose: (
    role: RoleBoard,
    candidate: SchedulingCandidate,
    slotLabels: string[],
    channel: OfferChannel,
    email: OfferedEmail | null
  ) => void;
  onBook: (role: RoleBoard, candidate: SchedulingCandidate, payload: BookPayload, chainLabel: string) => void;
  /** Take back times already given to the candidate. Records only — see the modal. */
  onWithdraw: (candidate: SchedulingCandidate) => void;
  /**
   * The debrief's outcome: move to offer, or archive with a rejection. Replaces
   * the old `onDebriefDone`, which could only record that it had happened.
   */
  onDebriefDecision: (
    role: RoleBoard,
    candidate: SchedulingCandidate,
    outcome: DebriefOutcome
  ) => void;
  /** Send the post-booking summary as the recruiter, behind the countdown. */
  onSendSummary: (candidate: SchedulingCandidate, email: { subject: string; body: string }) => void;
}

export function FinalRoundModals({
  modal,
  senderFirstName,
  gmailConnected,
  boardEmail,
  onClose,
  handlers,
}: {
  modal: FinalRoundModalState | null;
  senderFirstName: string;
  /** A Google account is connected on the board — the proposal can be sent. */
  gmailConnected: boolean;
  /** Whose board this is — the ask is sent on their behalf. */
  boardEmail: string;
  onClose: () => void;
  handlers: FinalRoundHandlers;
}) {
  if (!modal) return null;
  return (
    <Modal open onOpenChange={(open: boolean) => !open && onClose()}>
      <ModalContent className="!h-max !max-h-[calc(100dvh-2rem)] !w-[min(100vw-2rem,52rem)] gap-6 overflow-auto rounded p-6 md:p-14">
        <FinalRoundBody
          modal={modal}
          senderFirstName={senderFirstName}
          gmailConnected={gmailConnected}
          boardEmail={boardEmail}
          onClose={onClose}
          handlers={handlers}
        />
      </ModalContent>
    </Modal>
  );
}

/** NOT named "Modal…" — see the ActionModals note on DS child cloning. */
function FinalRoundBody({
  modal,
  senderFirstName,
  gmailConnected,
  boardEmail,
  onClose,
  handlers,
}: {
  modal: FinalRoundModalState;
  senderFirstName: string;
  gmailConnected: boolean;
  boardEmail: string;
  onClose: () => void;
  handlers: FinalRoundHandlers;
}) {
  switch (modal.kind) {
    case 'fr-ask':
      return (
        <AskPanelBody
          role={modal.role}
          candidate={modal.candidate}
          options={modal.options}
          boardEmail={boardEmail}
          onClose={onClose}
        />
      );
    case 'fr-config':
      return (
        <ConfigBody
          role={modal.role}
          suggestion={modal.suggestion}
          onConfirm={(config) => {
            handlers.onSaveConfig(modal.role, config);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'fr-propose':
      // Same shape as today's slot offers: multi-select + phone/email with an
      // editable German draft — just fed with chain options instead of slots.
      return (
        <OfferedBody
          key={modal.candidate.opportunityId}
          role={modal.role}
          candidate={modal.candidate}
          senderFirstName={senderFirstName}
          // Sends for real, like every other candidate email on the board. It
          // used to be pinned to false ("no send path yet"), which left the
          // final round as the one flow that stopped at a draft — the recruiter
          // then finished the job in Gmail, and the board's own log never saw it.
          gmailConnected={gmailConnected}
          slotsOverride={[
            ...modal.role.finalRounds.chains,
            ...(modal.role.finalRounds.moreChains || []),
          ].map(chainAsSlot)}
          preselected={modal.preselected}
          titlePrefix="Propose final round"
          confirmVerb="Mark proposed"
          onConfirm={(slotLabels, channel, email) => {
            handlers.onPropose(modal.role, modal.candidate, slotLabels, channel, email);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'fr-book':
      return (
        <BookBody
          key={modal.candidate.opportunityId}
          role={modal.role}
          candidate={modal.candidate}
          boardEmail={boardEmail}
          onConfirm={(payload, chainLabel) => {
            handlers.onBook(modal.role, modal.candidate, payload, chainLabel);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'fr-withdraw':
      /**
       * Taking back times already given to a candidate.
       *
       * The case section has had this since the slot-hold work; final rounds
       * never did, so a recruiter who proposed three options and then lost one
       * of the panel had no way to say so — the candidate stayed "proposed"
       * forever and the options read as still promised (Theresa, Sep 2026).
       *
       * Confirmation only: nothing was booked yet, so there is no calendar entry
       * and no candidate notice. Telling them the times are off is a message the
       * recruiter writes — the board must not guess that wording.
       */
      return (
        <>
          <ModalHeader>
            <ModalTitle>Withdraw the proposed times — {modal.candidate.name}?</ModalTitle>
          </ModalHeader>
          <div className="space-y-2">
            <p className="text-sm text-gray-600">
              {modal.candidate.name} goes back to the top of the final-round list and the options
              are free to propose again. Nothing was booked, so no calendar entry is touched.
            </p>
            <p className="text-sm text-gray-600">
              If you already sent them these times, tell them yourself — the board does not write
              that message.
            </p>
          </div>
          <ModalActions
            confirmLabel="Withdraw the proposed times"
            onConfirm={() => {
              handlers.onWithdraw(modal.candidate);
              onClose();
            }}
            onCancel={onClose}
          />
        </>
      );
    case 'fr-debrief':
      /**
       * The debrief's OUTCOME, not just the fact of it.
       *
       * This was a bare "Mark debrief done" confirmation, which recorded that
       * the most expensive round in the process had happened and gave the
       * recruiter nowhere to say what came out of it.
       */
      return (
        <DebriefDecisionBody
          candidate={modal.candidate}
          stages={modal.stages}
          archiveReasons={modal.archiveReasons}
          gmailConnected={gmailConnected}
          onConfirm={(outcome) => {
            handlers.onDebriefDecision(modal.role, modal.candidate, outcome);
            onClose();
          }}
          onCancel={onClose}
        />
      );
    case 'fr-summary':
      return (
        <SummaryBody
          modal={modal}
          gmailConnected={gmailConnected}
          onSend={(email) => handlers.onSendSummary(modal.candidate, email)}
          onClose={onClose}
        />
      );
  }
}

// ───────────────────────────── BOOKING ─────────────────────────────

// ───────────────────────────── CANDIDATE SUMMARY ─────────────────────────────

/**
 * Post-booking summary — generated server-side AFTER booking so the Meet links
 * are real, edited here, and SENT from the board.
 *
 * Sending was the missing half: TA had Gmail connected and still had to finish
 * in their mail client, which meant the board's own log never recorded the one
 * email the candidate actually needs. It has sent since #155 — but the confirm
 * button read "Done" and quietly sent nothing whenever `canSend` was false, and
 * copy / mail-app sat beside it as equals, so the flow still read as "sending
 * does not work for the final round" (Theresa, Sep 2026). Sending is now the
 * confirm action, and the fallback appears only when it is impossible.
 */
function SummaryBody({
  modal,
  gmailConnected,
  onSend,
  onClose,
}: {
  modal: Extract<FinalRoundModalState, { kind: 'fr-summary' }>;
  gmailConnected: boolean;
  /** Hands the edited text to the board's countdown, which does the sending. */
  onSend: (email: { subject: string; body: string }) => void;
  onClose: () => void;
}) {
  /**
   * Per-language drafts, like the resend modal — the server rendered both at
   * booking time, so switching is instant and loses nothing on either side.
   * It opens on the role's own language rather than a hardcoded German.
   */
  const [language, setLanguage] = useState<DraftLanguage>(modal.summaryLanguage ?? 'de');
  const both = modal.summaries ?? { de: modal.summary, en: modal.summary };
  const [subjects, setSubjects] = useState({ de: both.de.subject, en: both.en.subject });
  const [bodies, setBodies] = useState({ de: both.de.body, en: both.en.body });
  const subject = subjects[language];
  const body = bodies[language];
  const setSubject = (v: string) => setSubjects((p) => ({ ...p, [language]: v }));
  const setBody = (v: string) => setBodies((p) => ({ ...p, [language]: v }));
  const email = modal.candidateEmail || modal.candidate.email;
  const canSend = gmailConnected && !!email;


  return (
    <>
      <ModalHeader>
        <ModalTitle>Candidate summary — {modal.candidate.name}</ModalTitle>
      </ModalHeader>
      <div className="space-y-3">
        <p className="text-sm text-gray-600">
          Booked — the links are live.{' '}
          {canSend
            ? 'Confirming sends this from your own Gmail after a 10-second countdown you can stop.'
            : gmailConnected
              ? 'Lever holds no email address for this candidate, so the board cannot send it.'
              : 'No Google account is connected, so the board cannot send it.'}
        </p>
        {/* No `hasEdits` guard: both drafts are held side by side, so switching
            shows the other one and an edit made in either survives. */}
        <LanguageToggle value={language} onChange={setLanguage} />
        <Input label="Subject" value={subject} onChange={(e) => setSubject((e.target as HTMLInputElement).value)} />
        <Textarea
          label="Summary draft"
          rows={5}
          textAreaClassName="min-h-[16rem]"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        {/* Only when the draft came from the Library — the fallback prose has nothing to edit there. */}
        <div className="mt-1 text-right">
          {/*
            The ACTIVE draft's key, not the server's chosen one. The two can
            differ: `buildCandidateSummary` falls back to in-code prose when a
            template's body is empty in that language — routine, since TA
            writes the German first — so a bilingual booking can carry a
            Library key on one side and null on the other. Bound to
            `modal.summary` the modal offered "edit this wording" for the
            German template while showing the English fallback.

            No `??` here: null is meaningful and hides the link, because
            in-code prose has nothing in the Library to edit.
          */}
          <EmailLibraryLink templateKey={both[language].templateKey} />
        </div>
        {/* Only when the board genuinely cannot send — see SendFallback. */}
        {!canSend && (
          <SendFallback
            subject={subject}
            body={body}
            candidateEmail={email}
            gmailConnected={gmailConnected}
          />
        )}
      </div>
      <ModalActions
        /**
         * "Done" used to close the modal and send nothing, with no hint that the
         * summary the candidate needs had not gone anywhere — which is why this
         * flow read as "sending does not work for the final round". The label
         * now says which of the two will happen.
         */
        confirmLabel={canSend ? `Send to ${email}` : 'Close without sending'}
        onConfirm={() => {
          if (canSend) onSend({ subject, body });
          onClose();
        }}
        onCancel={onClose}
        cancelLabel="Cancel"
      />
    </>
  );
}
